import React, { useEffect, useState } from 'react';
import { Eye, Trash2 } from 'lucide-react';
import { api } from '../lib/api';

interface Post {
  id: string;
  activity: string;
  description: string;
  user: string;
  well?: string;
  createdAt: string;
}

interface AdminPostsProps {
  searchQuery?: string;
}

const AdminPosts: React.FC<AdminPostsProps> = ({ searchQuery = '' }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  const fetchPosts = async () => {
    const params: any = { page, limit };
    if (searchQuery) params.search = searchQuery;
    try {
      const res = await api.activities.getAll(params);
      const activities = res.data.data.activities || [];
      const postsData = activities.map((a: any) => ({
        id: a.id,
        activity: a.activity,
        description: a.description,
        user: `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.trim(),
        well: a.well?.name || '',
        createdAt: new Date(a.createdAt).toLocaleDateString(),
      }));
      setPosts(postsData);
      setTotal(res.data.data.pagination?.totalCount || 0);
    } catch (e) {
      console.error('Failed to fetch posts', e);
    }
  };

  useEffect(() => { setPage(1); }, [searchQuery]);
  useEffect(() => { fetchPosts(); }, [page, searchQuery]);

  const totalPages = Math.ceil(total / limit) || 1;

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.activities.delete(id);
      fetchPosts();
    } catch (e) {
      console.error('Failed to delete post', e);
    }
  };

  return (
    <div className="space-y-6 text-white">
      <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Activity</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Description</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">User</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Well</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Date</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {posts.map(post => (
                <tr key={post.id} className="hover:bg-gray-800/50">
                  <td className="px-6 py-4 text-white font-medium">{post.activity}</td>
                  <td className="px-6 py-4 text-gray-300 truncate max-w-xs" title={post.description}>{post.description}</td>
                  <td className="px-6 py-4 text-gray-300">{post.user}</td>
                  <td className="px-6 py-4 text-gray-300">{post.well}</td>
                  <td className="px-6 py-4 text-gray-300">{post.createdAt}</td>
                  <td className="px-6 py-4 space-x-2">
                    <button
                      onClick={() => setSelectedPost(post)}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="View Details"
                    >
                      <Eye className="w-4 h-4 text-gray-400" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="Delete Post"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-400">
          Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} posts
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page === 1}
            className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`px-3 py-2 rounded-lg transition-colors ${page === p ? 'bg-white text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage(p => Math.min(p + 1, totalPages))}
            disabled={page === totalPages}
            className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* View Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">{selectedPost.activity}</h2>
            <p className="text-gray-300 mb-4 whitespace-pre-wrap">{selectedPost.description}</p>
            <div className="text-gray-400 text-sm space-y-1 mb-6">
              <p><span className="text-gray-500">User:</span> {selectedPost.user || 'N/A'}</p>
              {selectedPost.well && <p><span className="text-gray-500">Well:</span> {selectedPost.well}</p>}
              <p><span className="text-gray-500">Date:</span> {selectedPost.createdAt}</p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setSelectedPost(null)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPosts;
